import { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors';
import { GraphQLFormattedError } from 'graphql';
import { BraintreeError } from 'braintree-web';
import { path } from 'ramda';

export type GenericError = ErrorLike & {
  displayMessage?: string;
  reason?: string;
  result?: unknown;
  networkError?: { result?: unknown };
};

export const isGenericError = (error: unknown): error is GenericError =>
  typeof (error as GenericError)?.message === 'string';

type NetworkError = {
  title?: string;
  detail?: string | { message?: string }[];
};

const isBraintreeError = (error: unknown): error is BraintreeError =>
  error != null && typeof error === 'object' && 'name' in error && error.name === 'BraintreeError';

const getBraintreeErrorMessage = (error: BraintreeError): string | undefined => {
  if (error.code === 'THREEDS_LOOKUP_VALIDATION_ERROR')
    return path(
      ['details', 'originalError', 'details', 'originalError', 'error', 'message'],
      error
    );
};

export const getGraphQLErrors = (error: unknown) =>
  CombinedGraphQLErrors.is(error) ? error.errors : undefined;

export const getGraphQLErrorCode = (error: GraphQLFormattedError) =>
  (error as GraphQLFormattedError & { code?: string }).code ?? error.extensions?.code;

export const getNetworkErrorResult = (error: GenericError): unknown => {
  const networkError = error.networkError ?? error;
  if (ServerError.is(networkError)) {
    try {
      return JSON.parse(networkError.bodyText);
    } catch {
      return undefined;
    }
  }
  return networkError.result;
};

export const getNetworkErrors = (error: GenericError) =>
  (getNetworkErrorResult(error) as { errors?: NetworkError[] } | undefined)?.errors;

export const getNetworkErrorMessage = (error: GenericError) => {
  if (isBraintreeError(error)) return getBraintreeErrorMessage(error);
  const networkErrors = getNetworkErrors(error);
  const details = networkErrors?.[0]?.detail;
  return Array.isArray(details) ? details[0]?.message : details;
};
